import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from 'src/app/services/auth.service';
import { SharedService } from 'src/app/services/shared.service';
import { REPORT_LANGUAGES } from 'src/app/shared/helpers/data';

@Component({
    selector: 'app-generate-new-report',
    templateUrl: './generate-new-report.component.html',
    styleUrl: './generate-new-report.component.scss',
    standalone: false
})
export class GenerateNewReportComponent {
  @ViewChild('contentGenerate') contentGenerateModal!: TemplateRef<any>;
  @ViewChild('responseModal') responseModal!: TemplateRef<any>;

  @Input() page_source!: string;
  @Input() page_data!: any;
  @Input() activity_type!: string;
  @Output() updateReportsEvent = new EventEmitter<any>()
  
  generateForm!: FormGroup;
  submitted = false;
  spinner = false;
  success = false;
  reanalyze = false;

  languagesList = REPORT_LANGUAGES;
  classificationOptions = ['CONFIDENTIAL', 'NON CONFIDENTIAL'];

  templates: any[] = [];
  generate_report_url: string = '';
  reanalyze_url: string = '';
  message: string = '';

  contentGenerateModalRef: any;
  responseModalRef: any;

  entity: any = null;
  report_id: any = null;

  constructor(private formBuilder: FormBuilder, private modalService: NgbModal, private authService: AuthService, private sharedService: SharedService) {}

  openModal(analyze_version: number) {
    this.submitted = false;
    this.message = '';
    this.report_id = null;
    this.build_url();

    if (analyze_version < 2) {
      this.reanalyze = true;
      this.message = 'The video is analyzed with old version, you must re-analyze this video for generating report.';
      this.contentGenerateModalRef = this.modalService.open(this.responseModal, { size: 'lg', backdrop: 'static', keyboard : false });
    } else {
      this.generateForm = this.formBuilder.group({
        template: [null, [Validators.required]],
        languages: [[], [Validators.required]], // Multi-select stores array
        classification: [null, [Validators.required]],
        subject: ['', [Validators.maxLength(50)]],
        source_material: ['', [Validators.maxLength(50)]],
        video_context: ['', [Validators.required, Validators.maxLength(250)]]
      });
  
      this.templates = this.authService.currentClientValue?.client?.client_extra?.report_templates || [];
      this.contentGenerateModalRef = this.modalService.open(this.contentGenerateModal, { size: 'lg', backdrop: 'static', keyboard : false });
    }
  }

  get f() { return this.generateForm.controls; }

  onGenerateSubmit() {
    this.submitted = true;
    if (this.generateForm.invalid) {
      return;
    }
    
    this.spinner = true;
    
    // Prepare Data payload for your API
    this.generateForm.value.protocols = this.generateForm.value.template.protocols || ["Janus_protocol_2_6"];
    this.generateForm.value.subject = this.generateForm.value.subject === 'Anonymous' ? '' : this.generateForm.value.subject;

    this.generateForm.value.header_data = {
      "Classification": this.generateForm.value.classification || "CONFIDENTIAL",
      "Subject": this.generateForm.value.subject,
      "Prepared By": this.authService.currentUserValue.person.first_name + ' ' + this.authService.currentUserValue.person.last_name,
      "Source Material": this.generateForm.value.source_material || "Video Analysis"
    }
        
    this.sharedService.generateReport(this.generate_report_url, this.generateForm.value, this.activity_type).subscribe({
			next: (data) => {
        this.submitted = false;
        this.spinner = false;
        this.contentGenerateModalRef.close();
        this.success = true;
        this.message = data.message;
        this.entity = data.entity;
        this.report_id = data.report_id;
      	this.responseModalRef = this.modalService.open(this.responseModal, { size: 'lg', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.submitted = false;
				this.spinner = false;
				this.success = false;
        this.contentGenerateModalRef.close();
        this.message = err.message;
        this.responseModalRef = this.modalService.open(this.responseModal, { size: 'lg', backdrop: 'static', keyboard : false });
			}
		});
  }

  reanalyzeSubmit() {
    this.submitted = true;
    this.spinner = true;
    this.reanalyze = false;

    this.sharedService.reanalyze(this.reanalyze_url, this.activity_type).subscribe({
			next: (data) => {
        this.submitted = false;
        this.spinner = false;
        this.contentGenerateModalRef.close();
        this.success = true;
        this.entity = data.entity;
        this.message = data.message;
      	this.responseModalRef = this.modalService.open(this.responseModal, { size: 'lg', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.submitted = false;
				this.spinner = false;
				this.success = false;
        this.contentGenerateModalRef.close();
        this.message = err.message;
        this.responseModalRef = this.modalService.open(this.responseModal, { size: 'lg', backdrop: 'static', keyboard : false });
			}
		});
  }

  closeModal() {
		if (this.success) {
			this.updateReportsEvent.emit({ entity: this.entity, reportId: this.report_id });
		}
		
    if (this.contentGenerateModalRef) {
      this.contentGenerateModalRef.close();
    }

    if (this.responseModalRef) {
      this.responseModalRef.close();
    }
	}

  build_url() {
    switch (this.page_source) {
      case 'conversation':
        this.activity_type = 'conversation';
        this.generate_report_url = `conversation/generate-report/${this.page_data.project_id}/${this.page_data.conversation_id}`;
        this.reanalyze_url = `conversation/${this.page_data.project_id}/${this.page_data.conversation_id}`;
        break;
      case 'case':
        this.activity_type = 'conversation';
        this.generate_report_url = `case/generate-report/${this.page_data.project_id}/${this.page_data.case_id}/${this.page_data.conversation_id}`;
        this.reanalyze_url = `case/${this.page_data.project_id}/${this.page_data.case_id}/${this.page_data.conversation_id}`;
        break;
      case 'examinee':
        this.activity_type = 'examinee';
        this.generate_report_url = `examinee/generate-report/${this.page_data.project_id}/${this.page_data.examinee_id}`;
        this.reanalyze_url = `examinee/reanalyze/${this.page_data.project_id}/${this.page_data.examinee_id}`;
        break;
      case 'case_examinee':
        this.activity_type = 'examinee';
        this.generate_report_url = `case/generate-report/${this.page_data.project_id}/${this.page_data.assessment_id}/${this.page_data.examinee_id}`;
        this.reanalyze_url = `case/${this.page_data.project_id}/${this.page_data.assessment_id}/${this.page_data.examinee_id}`;
        break;
    
      default:
        break;
    }
  }

}
